import { Link, NavLink, useNavigate } from "react-router-dom";
import { AuthSession, clearSession, displayName } from "../lib/api.js";
import { usePlatformPermissions } from "../lib/usePermissions.js";
import EnvironmentBadge from "./EnvironmentBadge.js";
import NavigationSearch from "./NavigationSearch.js";
import WorkspaceSelector from "./WorkspaceSelector.js";
import { useSidebarCollapsed } from "./useSidebarCollapsed.js";
import { platformAdminNavigation } from "../navigation/platformAdminNavigation.js";
import { workspaceNavigation } from "../navigation/workspaceNavigation.js";
import { filterNavTree } from "../navigation/permissions.js";

function Avatar({ name }: { name: string }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return <span className="avatar" aria-hidden>{initial}</span>;
}

export default function AdminLayout({ session, children }: { session: AuthSession; children: React.ReactNode }) {
  const navigate = useNavigate();
  const who = displayName();
  const onSignOut = () => {
    clearSession();
    navigate("/login", { replace: true });
  };
  const { has, hasAny, loading, isSuperAdmin } = usePlatformPermissions();
  const { collapsed, toggle } = useSidebarCollapsed();
  // Resolver shape NavigationSearch / permissions helpers expect.
  const resolver = { isSuperAdmin, has, hasAny };
  // Sidebar is rendered from the typed platformAdminNavigation tree so the
  // 10 IA groups (including Data & Aggregations with TallyMetrics, Rollups
  // & indicators, Mapping console, Warehouse explorer, Lineage explorer)
  // are always discoverable. PLATFORM_SUPER_ADMIN short-circuits to the
  // full tree; narrow platform roles see only items they hold permissions
  // for via filterNavTree.
  const filteredNav = filterNavTree(platformAdminNavigation, resolver);

  return (
    <div className="shell shell-admin">
      <header className="shell-header shell-header-admin">
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar (≡)" : "Collapse sidebar"}
        >☰</button>
        <Link to="/admin" className="brand">
          <img src="/brand-mark-light.svg" alt="" />
          <span>TallyStream · Platform</span>
        </Link>
        <span className="header-chip" style={{ background: "#1f2937", color: "#f9fafb" }}>
          Platform administration
        </span>
        <EnvironmentBadge />
        {isSuperAdmin && <WorkspaceSelector />}
        <div className="shell-header-spacer" />
        <button
          type="button"
          className="header-search-trigger"
          onClick={() => {
            // Synthesize a `/` keypress so NavigationSearch's global
            // listener opens the modal — single source of truth.
            window.dispatchEvent(new KeyboardEvent("keydown", { key: "/" }));
          }}
          title="Search (press / )"
          aria-label="Search navigation"
        >
          <span aria-hidden>🔍</span>
          <kbd>/</kbd>
        </button>
        <span className="user-pill">
          <Avatar name={who} />
          <span>{who}</span>
          <span className="pill pill-info" style={{ textTransform: "uppercase" }}>
            {session.role === "admin" ? "Platform admin" : session.role}
          </span>
        </span>
        <button className="signout" type="button" onClick={onSignOut}>Sign out</button>
      </header>
      <NavigationSearch trees={[platformAdminNavigation, workspaceNavigation]} resolver={resolver} />

      <aside className="shell-sidebar" aria-label="Platform administration">
        {loading ? <div className="sidebar-section soft" style={{ padding: 12 }}>Loading…</div> : null}

        {filteredNav.groups.map((group) => (
          <div key={group.id}>
            <div className="sidebar-section">
              <div className="sidebar-section-title">{group.label}</div>
            </div>
            <nav className="sidebar-nav">
              {group.items.map((item) => {
                const isIndex = item.to === "/admin";
                return (
                  <NavLink key={item.to} to={item.to} end={isIndex}>
                    {item.label}
                    {item.status === "coming-soon" ? (
                      <span className="pill pill-info" style={{ marginLeft: 8, fontSize: 10 }}>soon</span>
                    ) : null}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        ))}
      </aside>

      <main className="shell-main">{children}</main>
    </div>
  );
}
